import React, { useContext, useState } from "react";
import * as clipboard from "clipboard-polyfill";

import "../../styles/RightRail.css";
import { RequestStatuses, EQUITY_CLASS, SHORT_PLAYER_NAMES } from "../../constants";
import { fetchOmahaEquity } from "../../actions";
import { validator } from "../../utils/validation";
import { getSerializedPlayer } from "../../utils/serializing";
import {
  OmahaContext,
  OmahaActionTypes,
  isComplete
} from "../../context/omahaStore";
import ShareIcon from "../ShareIcon";

function EquityRow(props) {
  const { player, equities, active, ready } = props;
  const fulfilled = equities.status === RequestStatuses.FULFILLED;
  const raw = fulfilled ? equities.equities[getSerializedPlayer(player)] : 0;
  const pending = equities.status === RequestStatuses.PENDING && ready;
  const failed = equities.status === RequestStatuses.REJECTED && ready;
  const percent = raw ? Math.round(raw * 100) + "%" : "_";
  const width = Math.max(0, Math.min(1, raw || 0)) * 100 + "%";
  return (
    <div className={active ? "equityRow equityRow--active" : "equityRow"}>
      <div>{SHORT_PLAYER_NAMES[player]}</div>
      <div className="equityBarTrack">
        {pending ? (
          <div
            className="equityBarSweep"
            role="progressbar"
            aria-label="Calculating equity"
          />
        ) : (
          <div className="equityBarFill" style={{ width }} />
        )}
      </div>
      <div style={{ textAlign: "right" }}>
        <div className={EQUITY_CLASS} style={failed ? { color: "#d03234" } : undefined}>
          {pending ? "…" : failed ? "ERR" : fulfilled && ready ? percent : "_"}
        </div>
      </div>
    </div>
  );
}

export default function OmahaRail(props) {
  const [state, dispatch] = useContext(OmahaContext);
  const [equities, setEquities] = useState({ status: RequestStatuses.INACTIVE });
  const [copied, setCopied] = useState("");

  const boardCheck = validator(state.board);
  const readyPlayers = state.players.filter(p => isComplete(state.hands[p]));
  // The API needs at least two complete hands and a legal board to answer.
  const canCalculate = readyPlayers.length >= 2 && boardCheck.isValid;

  function calculate() {
    if (!canCalculate) {
      return;
    }
    const hands = {};
    readyPlayers.forEach(p => {
      hands[p] = state.hands[p];
    });
    fetchOmahaEquity(hands, state.board, setEquities);
  }

  function share() {
    clipboard
      .writeText(window.location.href)
      .then(() => setCopied("Copied! Go share the URL!"))
      .catch(() => setCopied("Error"));
  }

  const blocked =
    !boardCheck.isValid
      ? boardCheck.error
      : readyPlayers.length < 2
      ? "Give at least two players a full four-card hand."
      : equities.status === RequestStatuses.REJECTED && equities.error
      ? equities.error
      : "";

  return (
    <div className="rightRail">
      <div className="railSection">
        <span className="railLabel">Board</span>
        <input
          className="omahaBoardInput"
          placeholder="ex: AsKcQd"
          value={state.board}
          style={{
            boxSizing: "border-box",
            width: "100%",
            padding: "5px 6px",
            border: "1px solid #8fa0a8",
            borderRadius: "3px"
          }}
          onChange={e =>
            dispatch({
              type: OmahaActionTypes.UPDATE_BOARD,
              board: e.target.value
            })
          }
        />
      </div>

      <div className="railSection">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "62% minmax(0, 1fr)",
            gridColumnGap: ".4rem"
          }}
        >
          <button
            type="button"
            className="button railCalculate"
            disabled={!canCalculate}
            style={canCalculate ? undefined : { opacity: 0.5, cursor: "not-allowed" }}
            onClick={calculate}
          >
            Calculate
          </button>
          <button type="button" className="button railShare" onClick={share}>
            Share
            <div style={{ display: "flex", marginLeft: ".35rem" }}>
              <ShareIcon />
            </div>
          </button>
        </div>
        {copied ? (
          <div style={{ marginTop: ".4rem", color: "#daffba", fontSize: ".7rem" }}>
            {copied}
          </div>
        ) : null}
        {blocked ? <div className="omahaError">{blocked}</div> : null}
      </div>

      <div className="railSection railSection--grow">
        <span className="railLabel">Equity · monte carlo</span>
        <div className="railPanel">
          {state.players.map(player => (
            <EquityRow
              key={player}
              player={player}
              equities={equities}
              active={player === state.selected}
              ready={isComplete(state.hands[player])}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
